import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, Volume2, VolumeX } from 'lucide-react';

type NotificationType = 'success' | 'error' | 'info';

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
}

interface NotificationContextType {
  showNotification: (type: NotificationType, message: string) => void;
  isMuted: boolean;
  toggleMute: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isMuted, setIsMuted] = useState(() => {
    // Load mute state from localStorage
    const saved = localStorage.getItem('notificationsMuted');
    return saved === 'true';
  });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const recentNotifications = useRef<Set<string>>(new Set());
  const isMutedRef = useRef(isMuted);
  const audioInitialized = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Initialize audio element on first user interaction
  const initializeAudio = useCallback(() => {
    if (!audioInitialized.current) {
      try {
        const audio = new Audio('/notification.wav');
        audio.volume = 0.5;
        audio.preload = 'auto';

        // Create an AudioContext to bypass autoplay restrictions
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContext && !audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }

        audio.load();
        audioRef.current = audio;
        audioInitialized.current = true;
      } catch {
        // Audio initialization failed - not critical
      }
    }
  }, []);

  // Add click listener to initialize audio on first interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      initializeAudio();
      // Remove listener after first interaction
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('keydown', handleFirstInteraction);

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [initializeAudio]);

  // No periodic cleanup - we'll remove hashes individually after a delay

  const playNotificationSound = useCallback(() => {
    if (isMutedRef.current) {
      return;
    }

    // Resume AudioContext if suspended (helps with autoplay restrictions)
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }

    // Initialize audio if not already done
    if (!audioInitialized.current) {
      initializeAudio();
    }

    if (audioRef.current) {
      try {
        // Clone and play to ensure it plays even if already playing
        const audio = audioRef.current.cloneNode(true) as HTMLAudioElement;
        audio.volume = 0.5;

        const playPromise = audio.play();

        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Silently ignore audio play errors (autoplay restrictions, unsupported format, etc.)
            // This is not critical functionality
          });
        }
      } catch {
        // Silently ignore audio errors
      }
    }
  }, [initializeAudio]);

  const showNotification = useCallback((type: NotificationType, message: string) => {
    // Create a hash of the notification to prevent duplicates
    const notificationHash = `${type}-${message}`;

    // If this exact notification was shown recently, skip it
    if (recentNotifications.current.has(notificationHash)) {
      return;
    }

    // Add to recent notifications
    recentNotifications.current.add(notificationHash);

    // Remove this hash after 15 seconds (longer than polling interval)
    setTimeout(() => {
      recentNotifications.current.delete(notificationHash);
    }, 15000);

    const id = Math.random().toString(36).substr(2, 9);
    setNotifications((prev) => [...prev, { id, type, message }]);

    // Play notification sound for each unique notification
    playNotificationSound();

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  }, [playNotificationSound]);

  const toggleMute = () => {
    setIsMuted((prev) => {
      const newValue = !prev;
      localStorage.setItem('notificationsMuted', String(newValue));
      return newValue;
    });
  };

  const dismissNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-emerald-600" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-600" />;
    }
  };

  const getStyles = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return 'bg-emerald-50 border-emerald-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <NotificationContext.Provider value={{ showNotification, isMuted, toggleMute }}>
      {children}

      {/* Notification toasts - top right */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg animate-slide-in ${getStyles(notification.type)}`}
            style={{
              animation: 'slideIn 0.3s ease-out',
            }}
          >
            {getIcon(notification.type)}
            <span className="text-sm font-medium text-gray-900 flex-1">
              {notification.message}
            </span>
            <button
              onClick={() => dismissNotification(notification.id)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </NotificationContext.Provider>
  );
}

// Separate MuteButton component that can be used in page headers
export function MuteButton() {
  const { isMuted, toggleMute } = useNotification();

  return (
    <button
      onClick={toggleMute}
      className="p-2 bg-white rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors"
      title={isMuted ? 'Unmute notifications' : 'Mute notifications'}
    >
      {isMuted ? (
        <VolumeX className="h-5 w-5 text-gray-600" />
      ) : (
        <Volume2 className="h-5 w-5 text-cyan-600" />
      )}
    </button>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}
